import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';

export default function BudgetPage() {
  const { toast } = useToast();
  const [forecast, setForecast] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [populating, setPopulating] = useState(false);

  const load = () => {
    api.get('budget-forecast')
      .then(d => setForecast(Array.isArray(d) ? d : []))
      .catch(() => setForecast([]));
  };
  useEffect(load, []);

  const populate = async () => {
    if (forecast.length > 0 && !window.confirm('This overwrites every month currently shown with figures computed from live data (invoiced turnover, PO/payroll/overhead spend, order book, pipeline). Continue?')) return;
    setPopulating(true);
    try {
      await api.post('budget-forecast/populate', {});
      toast.success('Populated the fiscal year from live data.');
      load();
    } catch (e) {
      toast.error(e.message || 'Could not populate from live data.');
    }
    setPopulating(false);
  };

  const addMonth = async (data) => {
    if (!/^\d{4}-\d{2}$/.test((data.month || '').trim())) {
      alert('Please enter the month as YYYY-MM (e.g. 2026-04).');
      return;
    }
    if ([data.revenue, data.hardware_cost, data.resource_cost, data.overheads].some(v => v < 0)) {
      alert('Amounts cannot be negative.');
      return;
    }
    try { await api.post('budget-forecast', data); setShowAdd(false); load(); }
    catch (e) { alert(e.message || 'Could not save the forecast month.'); }
  };
  const tot = forecast.reduce((a, f) => ({
    rev: a.rev + (f.revenue || 0), pipe: a.pipe + (f.pipeline_revenue || 0),
    hw: a.hw + (f.hardware_cost || 0), res: a.res + (f.resource_cost || 0), oh: a.oh + (f.overheads || 0),
  }), { rev: 0, pipe: 0, hw: 0, res: 0, oh: 0 });
  const totProfit = tot.rev - tot.hw - tot.res - tot.oh;
  const totMargin = tot.rev > 0 ? (totProfit / tot.rev * 100).toFixed(1) : '0.0';
  const maxProfit = Math.max(...forecast.map(f => Math.abs(f.revenue - f.hardware_cost - f.resource_cost - f.overheads)), 1);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div><h2 className="text-base font-extrabold text-navy">Budget Forecast</h2><div className="text-[9px] text-gray-500">12-month P&L projection — fiscal year 1 Dec – 30 Nov</div></div>
        <div className="flex gap-2">
          <button onClick={populate} disabled={populating} className="px-3 py-1.5 border border-navy text-navy text-[10px] font-semibold rounded hover:bg-navy/5 disabled:opacity-40">
            {populating ? 'Populating…' : '↻ Populate from live data'}
          </button>
          <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ Add Month</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Committed Revenue</div><div className="text-lg font-extrabold text-navy">{formatMoney(tot.rev)}</div></div>
        <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">+ Pipeline Upside</div><div className="text-lg font-extrabold text-indigo-500">{formatMoney(tot.pipe)}</div></div>
        <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Costs</div><div className="text-lg font-extrabold text-navy">{formatMoney(tot.hw + tot.res + tot.oh)}</div></div>
        <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Profit</div><div className="text-lg font-extrabold text-emerald-500">{formatMoney(totProfit)}</div></div>
        <div className="bg-white border border-gray-200 rounded p-2"><div className="text-[8px] text-gray-500 uppercase font-bold">Margin</div><div className="text-lg font-extrabold text-emerald-500">{totMargin}%</div></div>
      </div>
      <div className="text-[8px] text-gray-400 mb-2">
        Committed Revenue = invoiced actuals (past months) + contracted order-book milestones not yet invoiced (future months). Pipeline Upside is a separate, weighted (value × stage probability) estimate from open opportunities — not included in Profit/Margin above, since it isn't won yet.
      </div>
      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full"><thead><tr>
          <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Month</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Revenue</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">+ Pipeline</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">HW Cost</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Resource</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Overheads</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Profit</th>
          <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Margin</th>
        </tr></thead>
        <tbody>{forecast.map(f => {
          const cost = (f.hardware_cost || 0) + (f.resource_cost || 0) + (f.overheads || 0);
          const profit = (f.revenue || 0) - cost;
          const margin = f.revenue > 0 ? (profit / f.revenue * 100).toFixed(1) : '0.0';
          return (
            <tr key={f.month} className="hover:bg-gold/5" title={f.notes || ''}>
              <td className="px-2 py-1 text-[10px] font-semibold whitespace-nowrap">
                {f.month}
                <span className={`ml-1.5 text-[7px] font-bold px-1 py-0.5 rounded ${f.is_actual ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {f.is_actual ? 'ACTUAL' : 'FORECAST'}
                </span>
              </td>
              <td className="px-2 py-1 text-[10px] font-semibold text-right">{formatMoney(f.revenue)}</td>
              <td className="px-2 py-1 text-[10px] text-right text-indigo-500">{f.pipeline_revenue ? formatMoney(f.pipeline_revenue) : '—'}</td>
              <td className="px-2 py-1 text-[10px] text-right">{formatMoney(f.hardware_cost)}</td>
              <td className="px-2 py-1 text-[10px] text-right">{formatMoney(f.resource_cost)}</td>
              <td className="px-2 py-1 text-[10px] text-right">{formatMoney(f.overheads)}</td>
              <td className={`px-2 py-1 text-[10px] font-semibold text-right ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatMoney(profit)}</td>
              <td className={`px-2 py-1 text-[10px] font-semibold text-right ${parseFloat(margin) >= 20 ? 'text-emerald-600' : 'text-red-600'}`}>{margin}%</td>
            </tr>
          );
        })}
        {forecast.length > 0 && <tr className="bg-gray-50 font-bold">
          <td className="px-2 py-1 text-[10px] font-bold">TOTAL</td>
          <td className="px-2 py-1 text-[10px] font-bold text-right">{formatMoney(tot.rev)}</td>
          <td className="px-2 py-1 text-[10px] text-right text-indigo-500">{formatMoney(tot.pipe)}</td>
          <td className="px-2 py-1 text-[10px] text-right">{formatMoney(tot.hw)}</td>
          <td className="px-2 py-1 text-[10px] text-right">{formatMoney(tot.res)}</td>
          <td className="px-2 py-1 text-[10px] text-right">{formatMoney(tot.oh)}</td>
          <td className="px-2 py-1 text-[10px] font-bold text-right text-emerald-600">{formatMoney(totProfit)}</td>
          <td className="px-2 py-1 text-[10px] font-bold text-right text-emerald-600">{totMargin}%</td>
        </tr>}
        {forecast.length === 0 && <tr><td colSpan="8" className="text-center py-6 text-[10px] text-gray-400">No budget forecast data. Click "Populate from live data" or add months manually.</td></tr>}
        </tbody></table>
      </div>
      {/* Bar chart */}
      {forecast.length > 0 && (
        <div className="bg-white border border-gray-200 rounded mt-3 p-3">
          <h3 className="text-[10px] font-bold mb-2">Monthly Profit</h3>
          <div className="flex items-end gap-1" style={{ height: 80 }}>
            {forecast.map(f => {
              const profit = (f.revenue || 0) - (f.hardware_cost || 0) - (f.resource_cost || 0) - (f.overheads || 0);
              const h = Math.max(4, Math.abs(profit) / maxProfit * 70);
              return (
                <div key={f.month} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="text-[7px] font-semibold" style={{ color: profit >= 0 ? '#10B981' : '#EF4444' }}>{Math.round(profit / 1000)}k</div>
                  <div style={{ width: '100%', height: h, background: profit >= 0 ? '#10B981' : '#EF4444', borderRadius: '2px 2px 0 0', opacity: 0.6 }} />
                  <div className="text-[6px] text-gray-400">{f.month.split('-')[1]}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showAdd && <Modal title="Add Forecast Month" onClose={() => setShowAdd(false)} footer={<><button onClick={() => setShowAdd(false)} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button><button onClick={() => { const f = document.getElementById('bf-form'); addMonth({ month: f.m.value, revenue: parseFloat(f.r.value) || 0, hardware_cost: parseFloat(f.h.value) || 0, resource_cost: parseFloat(f.rc.value) || 0, overheads: parseFloat(f.o.value) || 0 }); }} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Save</button></>}>
        <form id="bf-form"><div className="grid grid-cols-2 gap-2">
          <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Month (YYYY-MM)</label><input name="m" className="form-input" placeholder="2026-04" /></div>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Revenue</label><input name="r" type="number" step="0.01" className="form-input" /></div>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Hardware Cost</label><input name="h" type="number" step="0.01" className="form-input" /></div>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Resource Cost</label><input name="rc" type="number" step="0.01" className="form-input" /></div>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Overheads</label><input name="o" type="number" step="0.01" className="form-input" /></div>
        </div></form>
      </Modal>}
    </div>
  );
}
